import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { fetchMaterials } from '@hub/bearing-hub/common/materials/fetchMaterials';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type {
  IbhBearingMaterialsParams,
  IbhMaterialResponseDto,
  IbhProductionMethods,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { IbhFilterValues } from '@hub/hooks/states/useIbhFilterValuesStore';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import { getQueryableMaterials } from '@hub/bearing-hub/common/materials/materialFilters';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

export type MaterialFilterOptions = {
  value: string;
  label: string;
};

export type UseMaterialsOptions = {
  shapeKey: string;
  baseMaterials: IbhMaterialResponseDto[];
  filterValues: IbhFilterValues;
  session: Session;
  productionMethods?: IbhProductionMethods[];
  enabled?: boolean;
};

export type UseMaterials = {
  filteredMaterials: IbhMaterialResponseDto[];
  materialFilterOptions: MaterialFilterOptions[];
  selectedMaterials: string[];
};

export const usMarketMaterials = [
  'A181',
  'A351',
  'A500',
  'G',
  'H1',
  'H370',
  'J',
  'J3',
  'J350',
  'W300',
  'M250',
  'P',
  'P210',
  'Q2',
  'R',
  'X',
  'Z',
];

const sortByName = (items: IbhMaterialResponseDto[]): IbhMaterialResponseDto[] =>
  [...items].sort((a, b) => a.name.localeCompare(b.name));

export const useMaterials = (options: UseMaterialsOptions): UseMaterials => {
  const { shapeKey, baseMaterials, productionMethods, session, filterValues, enabled = true } = options;
  const selectedMaterials = (filterValues.material as string[] | undefined) || [];
  const countryCode = session?.country;

  const shouldFetch =
    Boolean(enabled) && Boolean(countryCode) && Boolean(shapeKey) && Boolean(productionMethods?.length);

  const { data } = useQuery({
    queryKey: ['materials', countryCode, shapeKey, productionMethods?.join(',')],
    queryFn: async () =>
      fetchMaterials({
        countryCode,
        shape: shapeKey,
        productionMethods,
      } as IbhBearingMaterialsParams),
    enabled: shouldFetch,
    gcTime: publicRuntimeConfig.ibhUseQueryCacheTtl,
    refetchOnWindowFocus: false,
  }) as UseQueryResult<IbhMaterialResponseDto[]>;

  const filteredMaterials = getQueryableMaterials(data || baseMaterials, filterValues, session);
  const materials = useMemo<IbhMaterialResponseDto[]>(
    () => sortByName(filteredMaterials),
    [data, baseMaterials, filterValues]
  );

  const materialFilterOptions = useMemo<MaterialFilterOptions[]>(() => {
    const seen = new Set<string>();
    return materials
      .map(item => ({
        label: item.name.replace(/-CNC/gi, ''),
        value: item.id,
      }))
      .filter(opt => {
        if (seen.has(opt.label)) {
          return false;
        }
        seen.add(opt.label);
        return true;
      });
  }, [materials]);

  return {
    filteredMaterials: materials,
    materialFilterOptions,
    selectedMaterials,
  };
};
